import { useCallback, useMemo, useState } from "react";

export interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
}

export interface UseColumnVisibilityReturn {
  visibility: Record<string, boolean>;
  toggle: (key: string) => void;
  reset: () => void;
  visibleColumns: ColumnDef[];
  visibleKeys: string[];
  isVisible: (key: string) => boolean;
  minReached: boolean;
}

function buildDefaults(columns: ColumnDef[]): Record<string, boolean> {
  return Object.fromEntries(
    columns.map((c) => [c.key, c.defaultVisible !== false]),
  );
}

function readStored(
  storageKey: string,
  defaults: Record<string, boolean>,
): Record<string, boolean> {
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    if (parsed && typeof parsed === "object") {
      return { ...defaults, ...parsed };
    }
  } catch {
    // ignore
  }
  return defaults;
}

const MIN_VISIBLE = 2;

export function useColumnVisibility(
  pageKey: string,
  columns: ColumnDef[],
): UseColumnVisibilityReturn {
  const storageKey = `columns_${pageKey}`;
  const defaults = useMemo(() => buildDefaults(columns), [columns]);
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() =>
    readStored(storageKey, defaults),
  );

  const persist = useCallback(
    (next: Record<string, boolean>) => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore
      }
    },
    [storageKey],
  );

  const toggle = useCallback(
    (key: string) => {
      setVisibility((prev) => {
        const currentlyVisible = prev[key];
        const visibleCount = Object.values(prev).filter(Boolean).length;
        if (currentlyVisible && visibleCount <= MIN_VISIBLE) return prev;
        const next = { ...prev, [key]: !currentlyVisible };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setVisibility(defaults);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [defaults, storageKey]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibility[c.key]),
    [columns, visibility],
  );

  const visibleKeys = useMemo(
    () => visibleColumns.map((c) => c.key),
    [visibleColumns],
  );

  const visibleCount = visibleColumns.length;

  const isVisible = useCallback(
    (key: string) => Boolean(visibility[key]),
    [visibility],
  );

  return {
    visibility,
    toggle,
    reset,
    visibleColumns,
    visibleKeys,
    isVisible,
    minReached: visibleCount <= MIN_VISIBLE,
  };
}
